import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Compass } from "lucide-react";
import { logo } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function NotFound() {
  const { t } = useLang();

  return (
    <div className="noise relative min-h-screen overflow-hidden bg-background text-foreground flex flex-col" data-testid="notfound-page">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[38rem] w-[38rem] rounded-full bg-primary/20 blur-[150px] animate-pulse-glow" />
      <div className="pointer-events-none absolute -bottom-52 -left-40 h-[34rem] w-[34rem] rounded-full bg-accent/10 blur-[150px]" />

      {/* top bar */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10 h-24 flex items-center justify-between">
        <Link to="/" data-testid="notfound-logo-home" className="flex items-center group">
          <img src={logo} alt="LocalNova Web Design" className="h-16 md:h-20 w-auto transition-transform duration-500 group-hover:scale-105 drop-shadow-[0_0_30px_hsl(180_80%_45%/0.4)]" />
        </Link>
        <LanguageSwitcher testid="lang-switcher-404" />
      </div>

      {/* content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="mx-auto w-full max-w-4xl px-6 md:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative inline-flex items-center justify-center"
          >
            <motion.span
              aria-hidden
              animate={{ rotate: 360 }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
              className="absolute -z-10 h-56 w-56 md:h-72 md:w-72 rounded-full border border-dashed border-primary/25"
            />
            <motion.span
              aria-hidden
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -z-10 flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-2xl bg-primary/10 text-primary"
              style={{ left: "8%", top: "10%" }}
            >
              <Compass className="h-8 w-8" />
            </motion.span>

            <h1 className="font-heading font-black tracking-tighter text-[7rem] sm:text-[10rem] lg:text-[13rem] leading-none text-glow">
              <span className="text-foreground">4</span>
              <motion.span
                className="inline-block text-primary"
                animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.05, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                0
              </motion.span>
              <span className="text-foreground">4</span>
            </h1>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-4 font-heading text-2xl sm:text-4xl font-bold tracking-tight"
          >
            {t.notfound.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.32 }}
            className="mx-auto mt-5 max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed"
          >
            {t.notfound.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.44 }}
            className="mt-10"
          >
            <Link
              to="/"
              data-testid="notfound-home-button"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground hover:bg-accent transition-colors duration-300"
              style={{ boxShadow: "0 0 60px -18px hsl(174 82% 44% / 0.7)" }}
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              {t.notfound.button}
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
